import {SearchIcon, TruckIcon, UserAddIcon} from '@heroicons/react/outline';
import {normalizeEmails} from '../../../utils/emailUtils';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutationWrapper} from '../../../common/api';
import searchIcon from '../../../components/assets/search.svg';
import {RequestTypeFromCheckboxLabelMapping} from '../../../components/atoms/RequestTypes';
import {Theme} from '../../../components/atoms/theme';
import {TitleM} from '../../../components/atoms/Typography';
import {ContentWrapper} from '../../../components/ContentWrapper';
import {Breadcrumb} from '../../../components/molecules/Breadcrumb';
import {CertificationModal} from '../../../components/molecules/CertificationModal';
import {DateOfServicePicker, validateDateOfService} from '../../../components/molecules/DateOfServicePicker';
import Form from '../../../components/organisms/Form';
import {useUser} from '../../../hooks/useLogin';
import {useRequestCertification} from '../../../hooks/useRequestCertification';
import {useSearchFirmsByName} from '../../../redux/reduxQuery/firmApi';
import {useSearchProvidersByName} from '../../../redux/reduxQuery/medicalProviderApi';
import {useSearchPatientsByName} from '../../../redux/reduxQuery/patientApi';
import {useCreateRequestMutation} from '../../../redux/reduxQuery/requestApi';

function AdminCreateRequest() {
  const [createRequest] = useCreateRequestMutation();
  const {showCertifiedRequestModal, showModal, buttonLoader, checked, setCheckbox, onCloseModal, requesterInfoInputsWithTogglefunction} =
    useRequestCertification();

  let navigate = useNavigate();
  const {pathname, ...location} = useLocation();
  const state = location.state as any;
  const [, setLoading] = useState(false);
  const isFromNestedForms = (pathname) => {
    return pathname === '/admin/patients/new' || pathname === '/admin/medical-providers/new';
  };
  const mutate = useMutationWrapper(
    createRequest,
    () => `Request Was Added Successfully!`,
    () => {
      setLoading(false);
      if (state?.cameFrom && (state?.cameFrom as string).includes('law-firm')) return navigate(state.cameFrom);
      else return navigate('/admin/requests');
    },
  );

  const user = useUser();

  const form = useForm();

  useEffect(() => {
    if (state) {
      if (isFromNestedForms(state.cameFrom)) {
        Object.entries(state.formData)?.map((formValue) => {
          formValue[0] === 'patient' && state.isCreatedNewPatient === true
            ? form.setValue('patient', {
                ...state.patient,
                value: state.patient.id,
                label: `${state.patient.name}-${state.patient.ssn}`,
              })
            : formValue[0] === 'provider' && state.isCreatedNewProvider === true
            ? form.setValue('provider', {
                ...state.provider,
                value: state.provider.id,
                label: state.provider.name,
              })
            : form.setValue(formValue[0], formValue[1]);
          return formValue;
        });
      }

      if (state.defaultValues?.firmId && state.defaultValues?.firmName)
        form.setValue('firmId', {
          id: state.defaultValues?.firmId,
          name: state.defaultValues?.firmName,
        });
    }

    // Initialize requester fields with logged-in user data if not already set
    if (user?.email && !form.getValues('requester')) {
      form.setValue('requester', user.name);
    }
    if (user?.email && !form.getValues('requesterEmails')) {
      form.setValue('requesterEmails', user.email);
    }
  }, [form.setValue, state, user?.email]);

  const [dosErrors, setDosErrors] = useState<{
    from?: string;
    to?: string;
  }>({});
  const dosFrom = form.watch('dateOfServiceFrom') || '';
  const dosTo = form.watch('dateOfServiceTo') || '';

  const searchProviders = useSearchProvidersByName();
  const searchPatients = useSearchPatientsByName();
  const searchFirms = useSearchFirmsByName();
  const onSubmit = form.handleSubmit(async (data) => {
    // Validate date of service fields
    const dateErrors = validateDateOfService(data.dateOfServiceFrom || '', data.dateOfServiceTo || '');
    if (dateErrors.from || dateErrors.to) {
      setDosErrors(dateErrors);
      return;
    }
    setDosErrors({});

    setLoading(true);
    const dataToSubmit = {
      firmId: data.firmId?.value || data.firmId,
      requestedBy: data.requester,
      requesterEmails: normalizeEmails(data.requesterEmails || ''),
      requestNotes: data.requestNotes,
      serviceDateFrom: data.dateOfServiceFrom || null,
      serviceDateTo: data.dateOfServiceTo || null,
      serviceDateNotes: data.serviceDateNotes || null,
      serviceDates:
        data.dateOfServiceFrom && data.dateOfServiceTo ? `${data.dateOfServiceFrom} - ${data.dateOfServiceTo}` : data.dateOfService || '',
      patientId: data.patient.id,
      providerId: data.provider.id,
      status: 'new',
      requestBills: !!data['Bills'],
      requestBillsCertified: !!data['Bills Certified'],
      requestRecords: !!data['Records'],
      requestHospitalistBilling: !!data['Hospitalist Billing'],
      requestHospitalistBillingCertified: !!data['Hospitalist Billing Certified'],
      requestRecordsCertified: !!data['Records Certified'],
      requestRadiology: !!data['CD of Radiology Images and Films'],
      requestRadiologyCertified: !!data['CD of Radiology Images and Films Certified'],
      requestERBilling: !!data['ER Physicians Billing'],
      requestERBillingCertified: !!data['ER Physicians Billing Certified'],
      requestRadiologyBilling: !!data['Radiology Billing'],
      requestRadiologyBillingCertified: !!data['Radiology Billing Certified'],
      requestHospitalAbstract: !!data['Hospital Abstract (in lieu of full recs)'],
      requestHospitalAbstractCertified: !!data['Hospital Abstract (in lieu of full recs) Certified'],
      requestAnesthesiaBilling: !!data['Anesthesia Billing'],
      requestAnesthesiaBillingCertified: !!data['Anesthesia Billing Certified'],
    };
    const medicalRequests = Object.keys(RequestTypeFromCheckboxLabelMapping).map((label) => {
      if (data[label]) {
        return {
          requestType: RequestTypeFromCheckboxLabelMapping[label],
          certified: !!data[`${label} Certified`],
        };
      }
      return null;
    });
    const filteredMedicalRequests = medicalRequests.filter((request) => request);
    return mutate({
      medicalRequests: filteredMedicalRequests,
      ...dataToSubmit,
    });
  });
  const crumbLinks = [
    {
      text: 'Requests',
      path: `/admin/requests`,
    },
    {
      text: 'New Request',
    },
  ];
  const formInputs = [
    {
      id: 'firmId',
      label: 'Firm',
      required: true,
      copy: false,
      select: true,
      asyncConfig: {
        loadOptions: searchFirms,
        getOptionLabel: (p: any) => p.name,
        getOptionValue: (p: any) => p.id,
        placeholder: 'Search Firms',
      },
      icon: true,
      img: searchIcon,
    },
    {
      id: 'patient',
      label: 'Patient',
      required: true,
      copy: false,
      select: true,
      addNewInDropDown: true,
      patientDetailed: true,
      asyncConfig: {
        loadOptions: searchPatients,
        getOptionLabel: (p: any) => p.name,
        getOptionValue: (p: any) => p.id,
        placeholder: 'Search Patients by Name or Case ID',
      },
      icon: true,
      img: searchIcon,
      addNewIcon: <UserAddIcon width={'0.938rem'} color={Theme.colors.primaries_000} />,
      addNewAction: () => {
        navigate('/admin/patients/new', {
          state: {
            formData: form.getValues(),
            cameFrom: pathname,
          },
        });
      },
    },
    {
      id: 'provider',
      label: 'Medical Provider',
      required: true,
      copy: false,
      tip: 'Search by address to ensure exact matches',
      noOptionsMessage: () => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
            }}
          >
            <div
              style={{
                borderRadius: '50%',
                backgroundColor: Theme.colors.neutrals_400,
                padding: '0.5rem',
              }}
            >
              <SearchIcon width={'1.5rem'} color={Theme.colors.neutrals_000} />
            </div>
            <span style={{color: Theme.colors.neutrals_000}}>Search for a medical provider</span>
            <span>Try searching by address, phone, or name</span>
          </div>
        );
      },
      select: true,
      addNewInDropDown: true,
      asyncConfig: {
        loadOptions: searchProviders,
        getOptionLabel: (p: any) => p.name,
        getOptionValue: (p: any) => p.id,
        placeholder: 'Search by address, phone, or name',
      },
      icon: true,
      img: searchIcon,
      detailed: true,
      addNewIcon: <TruckIcon width={'0.938rem'} color={Theme.colors.primaries_000} />,
      addNewAction: () => {
        navigate('/admin/medical-providers/new', {
          state: {
            formData: form.getValues(),
            cameFrom: pathname,
          },
        });
      },
    },
    {
      id: 'requester',
      label: 'Requester Name',
      required: true,
      copy: false,
      notFullLength: true,
    },
    {
      id: 'requesterEmails',
      label: 'Requester Email/emails',
      required: true,
      copy: true,
      notFullLength: false,
      message: 'Please enter requester email',
      type: 'emails',
      tooltipText: 'You may enter multiple email addresses separated by semicolons.',
    },
    requesterInfoInputsWithTogglefunction,
  ];

  return (
    <>
      <ContentWrapper height="100%">
        <div style={{marginBottom: '1.5rem'}} />

        <Breadcrumb crumbs={crumbLinks} />
        <div style={{marginBottom: '1.5rem'}} />

        <TitleM style={{marginBottom: '1.5rem'}}> New Request</TitleM>

        <div
          style={{
            background: '#fff',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: Theme.shadows.dropshadow_m,
            marginBottom: '5.625rem',
            display: 'flex',
            gap: '1.5rem',
          }}
        >
          {/* Left column - form fields */}
          <div style={{flex: 1}}>
            <Form
              {...form}
              onSubmit={onSubmit}
              formInputs={formInputs}
              numberOfColumns={1}
              customSubmit
              style={{
                boxShadow: 'none',
                padding: '0',
                marginBottom: '0',
                minWidth: 'none',
              }}
            />
          </div>

          {/* Right column - DOS + Notes + Buttons */}
          <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
            <DateOfServicePicker
              fromValue={dosFrom}
              toValue={dosTo}
              onFromChange={(val) => form.setValue('dateOfServiceFrom', val, {shouldDirty: true})}
              onToChange={(val) => form.setValue('dateOfServiceTo', val, {shouldDirty: true})}
              errors={dosErrors}
              notesValue={form.watch('requestNotes') || ''}
              onNotesChange={(val) => form.setValue('requestNotes', val, {shouldDirty: true})}
            />

            <div
              style={{
                marginTop: 'auto',
                display: 'flex',
                gap: '0.75rem',
                paddingTop: '1.5rem',
              }}
            >
              <button
                type="button"
                onClick={() => navigate('/admin/requests')}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: `1px solid ${Theme.colors.neutrals_300}`,
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: Theme.colors.primaries_000,
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      </ContentWrapper>

      {showModal && (
        <CertificationModal
          onClick={async () => await onCloseModal()}
          checked={checked}
          checkboxOnChange={() => setCheckbox(!checked)}
          disabled={!showCertifiedRequestModal}
          loader={buttonLoader}
        />
      )}
    </>
  );
}
export default AdminCreateRequest;
